// Share modal — shows the current /s/<token> URL, lets the user copy
// it, and revoke + reissue. Opens from the chat-actions drawer's
// "Share session" row.
//
// The token IS the credential — anyone with the URL can read the
// conversation. Revoke = generate a new token (old URL 404s).

import { useEffect, useState } from "preact/hooks";
import { Icon } from "../../icons";
import { api } from "../../lib/rpc";

interface ShareModalProps {
  open: boolean;
  onClose: () => void;
  sessionId: string | null;
  notify: (msg: string) => void;
}

export function ShareModal({ open, onClose, sessionId, notify }: ShareModalProps) {
  const [share, setShare] = useState<{ token: string; createdAt: number; url: string } | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!open || !sessionId) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    setCopied(false);
    api.sessionShareGet(sessionId)
      .then((s) => { if (!cancelled) setShare(s); })
      .catch((e) => { if (!cancelled) setError(e?.message || "failed to load share"); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [open, sessionId]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open || !sessionId) return null;

  const fullUrl = share ? `${location.origin}${share.url}` : "";

  const create = async () => {
    setLoading(true);
    setError(null);
    try {
      const s = await api.sessionShareCreate(sessionId);
      setShare(s);
    } catch (e: any) { setError(e?.message || "failed to create share"); }
    finally { setLoading(false); }
  };

  const revoke = async () => {
    setLoading(true);
    setError(null);
    try {
      await api.sessionShareRevoke(sessionId);
      setShare(null);
      notify("Share link revoked");
    } catch (e: any) { setError(e?.message || "failed to revoke"); }
    finally { setLoading(false); }
  };

  const copy = async () => {
    if (!fullUrl) return;
    try {
      await navigator.clipboard.writeText(fullUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 1400);
    } catch { notify("copy failed — select and copy manually"); }
  };

  return (
    <div className="share-bg" onClick={onClose}>
      <div className="share-modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-label="Share session">
        <header className="share-head">
          <span style={{ fontFamily: "var(--f-display)", fontWeight: 600, fontSize: 15 }}>
            Share session
          </span>
          <button className="icon-btn" onClick={onClose} title="Close"><Icon name="x" size={14} /></button>
        </header>

        <div className="share-body">
          <p className="share-sub">
            Anyone with this link can read the conversation. Revoke any time
            to invalidate the URL.
          </p>

          {error && <div className="share-err">{error}</div>}

          {loading && !share && (
            <p style={{ color: "var(--text-dim)", fontSize: 12 }}>loading…</p>
          )}

          {!loading && !share && (
            <button className="btn btn-primary share-create" onClick={create}>
              <Icon name="share" size={14} />
              <span>Create share link</span>
            </button>
          )}

          {share && (
            <>
              <div className="share-url">
                <code>{fullUrl}</code>
                <button className="btn share-copy" onClick={copy}>
                  <Icon name={copied ? "check" : "doc"} size={13} />
                  <span>{copied ? "Copied" : "Copy"}</span>
                </button>
              </div>
              <div className="share-actions">
                <button className="btn-ghost" onClick={revoke} disabled={loading}>
                  Revoke
                </button>
                <button className="btn" onClick={create} disabled={loading} title="Revoke this URL and issue a new one">
                  Reissue
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
